import { z } from 'zod'
import { MetricFilter, MetricFilterQuery } from '~/stores/types'

import { AnalyticsQueryFilter } from '@hap/analytics'
import { Timespan } from '@hap/utils'

// Filter schema definitions
export const FilterOpIn = z.object({
  op: z.literal('in'),
  values: z.string().array(),
})

export const DurationFilter = z.union([
  z.object({ op: z.literal('gte'), value: z.string(), field: z.literal('handleTime') }),
  z.object({ op: z.literal('lte'), value: z.string(), field: z.literal('handleTime') }),
  z.object({
    op: z.literal('between'),
    values: z.string().array(),
    field: z.literal('handleTime'),
  }),
])

export type DurationFilterType = z.infer<typeof DurationFilter>

export const dashboardFilters = z.object({
  channelIds: FilterOpIn.optional(),
  tagIds: FilterOpIn.optional(),
  groupIds: FilterOpIn.optional(),
  reportsToIds: FilterOpIn.optional(),
  intentIds: FilterOpIn.optional(),
  handleTime: DurationFilter.optional(),
})

export type DashboardFilters = z.infer<typeof dashboardFilters>

/**
 * Convert new filter format to legacy MetricFilter[] format
 * Used by TagGroupBreakdown and other legacy analytics components
 */
export function convertToMetricFilters(filters: DashboardFilters): MetricFilter[] {
  const result: MetricFilter[] = []

  if (filters.channelIds?.values?.length) {
    result.push({ key: 'contactChannelId', values: filters.channelIds.values })
  }
  if (filters.tagIds?.values?.length) {
    result.push({ key: 'tagIds', values: filters.tagIds.values })
  }
  if (filters.groupIds?.values?.length) {
    result.push({ key: 'groupIds', values: filters.groupIds.values })
  }

  return result
}

/**
 * Convert new filter format to legacy MetricFilterQuery[] format
 * Used by TagGroupBreakdown for complex queries like reports-to and duration
 */
export function convertToMetricFilterQueries(
  filters: DashboardFilters,
): MetricFilterQuery[] {
  const result: MetricFilterQuery[] = []

  if (filters.reportsToIds?.values?.length) {
    result.push({
      userReportsTo: {
        key: 'userId',
        recursive: true,
        supervisorIds: filters.reportsToIds.values,
      },
    })
  }

  if (filters.handleTime) {
    const duration = filters.handleTime
    if (duration.op === 'between' && 'values' in duration) {
      result.push({
        range: {
          key: 'handleTime',
          start: duration.values[0],
          end: duration.values[1],
        },
      })
    } else if ('value' in duration) {
      // For gte/lte, convert to range format
      result.push({
        range: {
          key: 'handleTime',
          ...(duration.op === 'gte'
            ? { start: duration.value }
            : { end: duration.value }),
        },
      })
    }
  }

  return result
}

/**
 * Convert new filter format to AnalyticsQueryFilter[] format
 * Used by MetricCell, ConversationDrivers, ServiceQualityOverview
 */
export function convertToAnalyticsFilters(
  filters: DashboardFilters,
): AnalyticsQueryFilter[] {
  const result: AnalyticsQueryFilter[] = []

  if (filters.channelIds?.values?.length) {
    result.push({ field: 'channelId', op: 'in', values: filters.channelIds.values })
  }
  if (filters.tagIds?.values?.length) {
    result.push({ field: 'tagIds', op: 'in', values: filters.tagIds.values })
  }
  if (filters.groupIds?.values?.length) {
    result.push({ field: 'groupIds', op: 'in', values: filters.groupIds.values })
  }
  if (filters.reportsToIds?.values?.length) {
    result.push({
      field: 'assignedSupervisorId',
      op: 'in',
      values: filters.reportsToIds.values,
    })
  }
  if (filters.intentIds?.values?.length) {
    result.push({ field: 'intentId', op: 'in', values: filters.intentIds.values })
  }
  if (filters.handleTime) {
    result.push(filters.handleTime as AnalyticsQueryFilter)
  }

  return result
}

/**
 * Build URL search params for Explore page with current filters
 * This allows users to seamlessly transition from Dashboard to Explore with active filters
 */
export function buildExploreSearchParams(
  filters: DashboardFilters,
  timespan: Timespan.Info,
): Record<string, unknown> {
  const analyticsFilters = convertToAnalyticsFilters(filters)

  return {
    filters: analyticsFilters,
    relativeTimespan: timespan,
  }
}

/**
 * Check if any filters are active
 */
export function hasActiveFilters(filters: DashboardFilters): boolean {
  return Object.values(filters).some((f) =>
    f && 'values' in f ? f.values.length > 0 : Boolean(f),
  )
}
